import type { SupabaseClient, User } from "@supabase/supabase-js";
import type { Database } from "../integrations/supabase/supabaseTypes";

export type OwnerKind = "anonymous" | "signedIn";

export interface EnsuredPlayerSession {
  readonly ownerId: string;
  readonly ownerKind: OwnerKind;
}

export interface EnsurePlayerSessionDeps {
  readonly client: SupabaseClient<Database>;
  readonly upsertProfile: (input: EnsuredPlayerSession) => Promise<void>;
}

export async function ensurePlayerSession(
  deps: EnsurePlayerSessionDeps,
): Promise<EnsuredPlayerSession> {
  const { client, upsertProfile } = deps;

  const sessionResult = await client.auth.getSession();
  if (sessionResult.error) {
    throw new Error(
      `Failed to read Supabase session: ${sessionResult.error.message}`,
    );
  }

  let user: User | null = sessionResult.data.session?.user ?? null;

  if (user === null) {
    const signInResult = await client.auth.signInAnonymously();
    if (signInResult.error) {
      throw new Error(
        `Failed to sign in anonymously: ${signInResult.error.message}`,
      );
    }
    user = signInResult.data.user;
    if (user === null) {
      throw new Error(
        "Anonymous sign-in returned no user. Check Supabase auth configuration.",
      );
    }
  }

  const ownerKind: OwnerKind =
    user.is_anonymous === true ? "anonymous" : "signedIn";
  const ownerId = user.id;

  await upsertProfile({ ownerId, ownerKind });

  return { ownerId, ownerKind };
}
